<template>
    <main class="flex-1 relative pb-8 z-0 overflow-y-auto">
        <PageHeader>
            <h1 class="ml-3 text-2xl font-bold leading-7 text-gray-900 sm:leading-9 sm:truncate flex flex-row justify-between w-full">
                <span>
                    <span class="flex flex-row">
                        Unité - {{ $route.params.id ? 'Modifier' : 'Créer' }}
                        <Loader v-if="isUnitFetchPending || isUnitSavePending"
                                class="-sm m-2"/>
                    </span>
                    <LangSwitcher/>
                </span>
            </h1>
        </PageHeader>

        <div class="mt-8">
            <div class="m-4">
                <form v-if="!isUnitFetchPending && unit !== null" class="max-w-screen-lg mx-auto" method="POST"
                      @submit.prevent :key="'unit-save-'+key">
                    <Input
                        id="name"
                        type="text"
                        name="name"
                        label="ID (nom par défaut)"
                        disabled
                        v-model="unit.name"
                        :errors="errors.name"
                        class="mb-4"
                    />

                    <Input
                        id="fullname"
                        type="text"
                        name="fullname"
                        label="Nom"
                        v-model="unit.translatable.fullname[locale]"
                        :errors="errors.fullname"
                        class="mb-4"
                    />

                    <Select
                        id="status_id"
                        name="status_id"
                        label="Status"
                        :options="statusOptions"
                        v-model="unit.status_id"
                        :errors="errors.status"
                        class="mb-4"
                    />

                    <Select
                        id="unit_id"
                        name="unit_id"
                        label="Unité parente"
                        :options="unitOptions"
                        :isPending="isUnitsFetchPending"
                        v-model="unit.unit_id"
                        :errors="errors.unit_id"
                        class="mb-4"
                    />

                    <Editor
                        id="description"
                        name="description"
                        label="Description"
                        v-model="unit.translatable.description[locale]"
                        :errors="errors.description"
                        class="mb-4"
                    />

                    <div class="grid grid-cols-2 gap-4 mb-2">
                        <Input
                            id="surface"
                            type="number"
                            name="surface"
                            label="Surface"
                            placeholder="10"
                            v-model="unit.surface"
                            :errors="errors.surface"
                            class="mb-2"
                            unit="m²"
                        />

                        <Input
                            id="price"
                            type="number"
                            name="price"
                            label="Prix"
                            placeholder="10"
                            v-model="unit.price"
                            :errors="errors.price"
                            class="mb-2"
                            unit="€"
                        />

                        <Input
                            id="price_detail"
                            type="text"
                            name="price_detail"
                            label="Détail du prix"
                            v-model="unit.translatable.price_detail[locale]"
                            :errors="errors.price_detail"
                            class="mb-4"
                        />

                        <Input
                            id="bedrooms"
                            type="number"
                            name="bedrooms"
                            label="Chambre(s)"
                            placeholder="3"
                            v-model="unit.bedrooms"
                            :errors="errors.bedrooms"
                            class="mb-2"
                            unit="ch."
                        />

                        <Select
                            id="category_id"
                            name="category_id"
                            label="Categorie"
                            :options="categoryOptions"
                            v-model="unit.category_id"
                            :errors="errors.category_id"
                            class="mb-4"
                        />
                    </div>

                    <div class="grid grid-cols-3 gap-2 mb-2">
                        <Switch
                            id="has_garden"
                            label="Jardin ?"
                            name="has_garden"
                            v-model="unit.has_garden"
                            :errors="errors.has_garden"
                            class="mb-2"
                            isInline
                        />

                        <Switch
                            id="has_terrace"
                            label="Terrasse ?"
                            name="has_terrace"
                            v-model="unit.has_terrace"
                            :errors="errors.has_terrace"
                            class="mb-2"
                            isInline
                        />

                        <Switch
                            id="has_balcony"
                            label="Balcon ?"
                            name="has_balcony"
                            v-model="unit.has_balcony"
                            :errors="errors.has_balcony"
                            class="mb-2"
                            isInline
                        />

                        <Switch
                            id="has_loggia"
                            label="Loggia ?"
                            name="has_loggia"
                            v-model="unit.has_loggia"
                            :errors="errors.has_loggia"
                            class="mb-2"
                            isInline
                        />
                    </div>

                    <div>
                        <div class="flex flex-row mb-12">
                            <FilePckr
                                id="blueprints2D"
                                name="blueprints2D"
                                label="Plan 2D"
                                multiple
                                @startLoad="handleStartLoadBlueprints2D"
                                @endLoad="handleEndLoadBlueprints2D"
                                class="w-1/3 pr-4"
                            />
                            <div class="w-2/3 grid grid-cols-2 gap-4">
                                <template v-if="unit.blueprints2D?.data">
                                    <div v-for="blueprint2D in unit.blueprints2D?.data" :key="index" class="relative">
                                        <svg-vue icon="times" class="absolute top-2 right-2 w-8 h-8 z-10 fill-red-500 cursor-pointer hover:opacity-85 hover:bg-white transition-all duration-300 rounded-full" @click="handleRemoveBlueprint2D(blueprint2D.uuid)" />
                                        <img :key="blueprint2D.uuid" class="relative object-cover w-full" :src="blueprint2D.url" alt="blueprint2D" />
                                    </div>
                                </template>
                            </div>
                        </div>

                        <hr class="mb-12 bg-slate-600"/>

                        <div class="flex flex-row mb-12">
                            <FilePckr
                                id="blueprint3D"
                                name="blueprint3D"
                                label="Plan 3D"
                                @startLoad="handleStartLoadBlueprint3D"
                                @endLoad="handleEndLoadBlueprint3D"
                                class="w-1/3 pr-4"
                            />
                            <div class="w-2/3">
                                <img v-if="unit.blueprint3D" class="relative object-cover w-full" :src="unit.blueprint3D" alt="">
                            </div>
                        </div>
                    </div>

                    <h3 class="text-lg font-bold">Plan PDF</h3>

                    <div class="flex flex-col items-center justify-center gap-4">
                        <a :href="unit.blueprints" v-if="unit.blueprints" class="disabled:opacity-50 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-custom-600 hover:bg-custom-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500" target="_blank"><strong>Télécharger le plan actuel</strong></a>

                        <strong v-if="unit.blueprints">OU</strong>

                        <FilePckr
                            id="blueprints"
                            name="blueprints"
                            :label="unit.blueprints ? 'Télécharger un nouveau plan' : 'Ajouter un plan'"
                            class="mb-4 w-full"
                            @startLoad="handleStartLoadBlueprints"
                            @endLoad="handleEndLoadBlueprints"
                        />
                    </div>

                    <button @click.prevent="handleSaveUnit"
                            type="button"
                            name="button"
                            class="disabled:opacity-50 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-custom-600 hover:bg-custom-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                            :disabled="isUnitSavePending || isFilepondPending"
                    >
                        Save
                    </button>
                </form>
            </div>
        </div>
    </main>
</template>

<script>
import PageHeader from "../../components/partials/page-header";
import Loader from "../../components/partials/loader";
import LangSwitcher from "../../components/partials/lang-switcher";

import Input from "../../components/form/input";
import Textarea from "../../components/form/textarea";
import FilePckr from "../../components/form/file-pckr";
import Editor from "../../components/form/editor";
import Select from "../../components/form/select";
import Switch from "../../components/form/switch";

export default {
    data() {
        return {
            key: 0,
            blueprints2D: [],
            blueprint3D: [],
            blueprints: [],
            isFilepondPending: false,
        }
    },
    async beforeMount() {
        if (this.$route.params.id) {
            await this.$store.dispatch('units/getSingle', {id: this.$route.params.id, includes: ['blueprints2D']})
        } else {
            await this.$store.dispatch('units/resetSingle')
        }

        await this.$store.dispatch('statuses/getList');
        this.$store.dispatch('categories/getList');
        this.$store.dispatch('units/getList', {search: { 'floor.building.project.id': this.unit.project.id }, per_page: 100});
    },
    watch: {
        locale() {
            this.key++
        }
    },
    computed: {
        isUnitFetchPending() {
            return this.$store.state.units.isFetchSinglePending
        },
        isUnitsFetchPending() {
            return this.$store.state.units.isFetchListPending
        },
        isUnitSavePending() {
            return this.$store.state.units.isSavePending;
        },
        unit() {
            return this.$store.state.units.single;
        },
        floor() {
            return this.$store.state.floors.single;
        },
        errors() {
            return this.$store.state.units.errors;
        },
        locale() {
            return this.$store.state.config.locale;
        },
        statuses() {
            return this.$store.state.statuses.list;
        },
        statusOptions() {
            return this.statuses.map((status) => {
                return {label: status.translatable.name[this.locale], value: status.id}
            })
        },
        categories() {
            return this.$store.state.categories.list;
        },
        categoryOptions() {
            return this.categories.map((status) => {
                return {label: status.name, value: status.id}
            })
        },
        units() {
            return this.$store.state.units.list;
        },
        unitOptions() {
            return this.units.map( unit => { return {label: unit.name, value: unit.id }});
        }
    },
    methods: {
        handleSaveUnit() {
            if (this.unit.id != null) {
                this.$store.dispatch('units/update', {
                    single: {
                        ...this.unit,
                        blueprints2D: this.blueprints2D,
                        blueprint3D: this.blueprint3D,
                        blueprints: this.blueprints,
                        description: {
                            fr: this.unit.translatable.description.fr,
                            en: this.unit.translatable.description.en,
                        },
                        fullname: {
                            fr: this.unit.translatable.fullname.fr,
                            en: this.unit.translatable.fullname.en,
                        },
                        floor_id: this.floor ? this.floor.id : this.unit.floor_id,
                        price_detail: {
                            fr: this.unit.translatable.price_detail.fr,
                            en: this.unit.translatable.price_detail.en
                        }
                    }
                }).then(() => {
                    if (Object.values(this.errors).length === 0) {
                        this.$router.push({name: 'floor-show', params: {id: this.floor ? this.floor.id : this.unit.floor_id}})
                    }
                })
            } else {
                this.$store.dispatch('units/store', {
                    single: {
                        ...this.unit,
                        blueprints2D: this.blueprints2D,
                        blueprint3D: this.blueprint3D,
                        blueprints: this.blueprints,
                        description: {
                            fr: this.unit.translatable.description.fr,
                            en: this.unit.translatable.description.en,
                        },
                        fullname: {
                            fr: this.unit.translatable.fullname.fr,
                            en: this.unit.translatable.fullname.en,
                        },
                        floor_id: this.floor.id,
                        price_detail: {
                            fr: this.unit.translatable.price_detail.fr,
                            en: this.unit.translatable.price_detail.en
                        }
                    }
                }).then(() => {
                    if (Object.values(this.errors).length === 0) {
                        this.$router.push({name: 'floor-show', params: {id: this.floor.id}})
                    }
                })
            }
        },
        handleStartLoadBlueprints2D(blueprint2D) {
            this.blueprints2D = blueprint2D.map( file => file.serverId);
            this.isFilepondPending = true;
        },
        handleEndLoadBlueprints2D(blueprint2D) {
            this.blueprints2D = blueprint2D.map( file => file.serverId);
            this.isFilepondPending = false;
        },
        handleStartLoadBlueprint3D(blueprint3D) {
            this.blueprint3D = blueprint3D.map( file => file.serverId);
            this.isFilepondPending = true;
        },
        handleEndLoadBlueprint3D(blueprint3D) {
            this.blueprint3D = blueprint3D.map( file => file.serverId);
            this.isFilepondPending = false;
        },
        handleStartLoadBlueprints(blueprints) {
            this.blueprints = blueprints.map( file => file.serverId);
            this.isFilepondPending = true;
        },
        handleEndLoadBlueprints(blueprints) {
            this.blueprints = blueprints.map( file => file.serverId);
            this.isFilepondPending = false;
        },
        handleRemoveBlueprint2D(uuid) {
            this.$store.dispatch('files/delete', {uuid: uuid}).then(() => {
                this.$store.dispatch('units/getSingle', {id: this.$route.params.id, includes: ['blueprints2D']});
            });
        }
    },
    components: {
        Switch,
        PageHeader,
        Input,
        Textarea,
        Loader,
        FilePckr,
        Editor,
        LangSwitcher,
        Select,
    }
}
</script>
